import { EventType } from "@/analytics/analytics-events";
import { OpenContextMenuEventProperties } from "@/analytics/context-menu-events-properties";
import { isIElementWaypoint } from "@/hooks/use-placeholders";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  IElement,
  isIElementGenericImgSheet,
  isIElementOdometryPathSection,
} from "@faro-lotv/ielement-types";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { Vector3 } from "three";
import { EditWaypointContextMenu } from "../edit-waypoints/edit-waypoint-context-menu/edit-waypoint-context-menu";
import { SheetContextMenu } from "../sheet-context-menu/sheet-context-menu";
import { PathContextMenu } from "./path-context-menu";

function getContextMenuVia(
  target: IElement,
): OpenContextMenuEventProperties["via"] | undefined {
  if (isIElementWaypoint(target)) {
    return "waypoint";
  } else if (isIElementGenericImgSheet(target)) {
    return "sheet";
  } else if (isIElementOdometryPathSection(target)) {
    return "path";
  }
}

type UseSceneContextMenuProps = {
  /** Callback invoked when the context menu is shown */
  onContextMenuShown?(target: IElement): void;
};

export type SceneContextMenuData = {
  /** The position of the clicked point */
  position?: Vector3;

  /** The element the context menu is requested for.*/
  target: IElement;
};

type UseSceneContextMenuReturn = {
  /** Callback to open the context menu */
  onSceneContextMenu(data: SceneContextMenuData): void;

  /** Component that should be placed in the scene to render the context menu. */
  sceneContextMenuComponent: ReactNode;
};

/**
 * Hook to manage the context menus in the scene.
 *
 * @returns a callback to be used when a context menu is requested in the scene, and the context menu component itself.
 */
export function useSceneContextMenu({
  onContextMenuShown,
}: UseSceneContextMenuProps = {}): UseSceneContextMenuReturn {
  const [sceneContextMenuData, setSceneContextMenuData] =
    useState<SceneContextMenuData>();

  const onSceneContextMenu = useCallback(
    ({ position, target }: SceneContextMenuData) => {
      const via = getContextMenuVia(target);
      Analytics.track<OpenContextMenuEventProperties>(
        EventType.openContextMenu,
        via ? { via } : undefined,
      );

      setSceneContextMenuData({ position, target });

      onContextMenuShown?.(target);
    },
    [onContextMenuShown],
  );

  const onClose = useCallback(() => {
    setSceneContextMenuData(undefined);
  }, []);

  const sceneContextMenuComponent = useMemo(() => {
    if (!sceneContextMenuData) return null;

    if (isIElementWaypoint(sceneContextMenuData.target)) {
      return (
        <EditWaypointContextMenu
          waypoint={sceneContextMenuData.target}
          onClose={onClose}
        />
      );
    } else if (
      isIElementGenericImgSheet(sceneContextMenuData.target) &&
      sceneContextMenuData.position
    ) {
      return (
        <SheetContextMenu
          position={sceneContextMenuData.position}
          sheetId={sceneContextMenuData.target.id}
          onClose={onClose}
        />
      );
    } else if (
      isIElementOdometryPathSection(sceneContextMenuData.target) &&
      sceneContextMenuData.position
    ) {
      return (
        <PathContextMenu
          position={sceneContextMenuData.position}
          path={sceneContextMenuData.target}
          onClose={onClose}
        />
      );
    }

    return null;
  }, [onClose, sceneContextMenuData]);

  return {
    onSceneContextMenu,
    sceneContextMenuComponent,
  };
}
